import { Shield, Users, Truck, Clock, DollarSign, Award, MapPin, HeartHandshake, PackageCheck, PackageOpen, Wrench, Zap, ChevronRight } from "lucide-react";
import ServicePageLayout from "@/components/ServicePageLayout";

const services = [
  { slug: "home-moves", title: "Home Moves", desc: "Full service house moves across every Melbourne suburb." },
  { slug: "apartment-moves", title: "Apartment Moves", desc: "High rise, walk up and lift access specialists." },
  { slug: "office-relocations", title: "Office Relocations", desc: "After hours commercial moves with minimal downtime." },
  { slug: "single-item-moves", title: "Single Item Moves", desc: "Fridges, sofas, pianos and Marketplace pickups." },
  { slug: "same-day-moves", title: "Same Day Moves", desc: "Urgent removals with priority dispatch." },
];

const ServicesBlock = (
  <section className="py-20 md:py-28 bg-white">
    <div className="container">
      <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center mb-14 tracking-tight">
        Services Available in Melbourne
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {services.map((s) => (
          <a
            key={s.slug}
            href={`/${s.slug}`}
            className="group bg-white/70 backdrop-blur-xl border border-[#1a1a1a]/10 shadow-[0_8px_30px_rgba(26,26,26,0.06)] rounded-2xl p-6 hover:border-[#1a1a1a]/30 transition-colors"
          >
            <div className="flex items-center gap-2 text-[#1a1a1a] font-bold text-lg">
              {s.title}
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
            <p className="text-[#1a1a1a]/70 text-sm mt-2 leading-relaxed">{s.desc}</p>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const MelbournePage = () => (
  <ServicePageLayout
    slug="melbourne"
    title="Removalists Melbourne: Local Moving Company | OZ MOVE CO."
    metaDescription="Melbourne removalists you can trust. Homes, apartments, offices and single items. Fully insured, transparent hourly pricing. Free quote from OZ MOVE CO."
    h1="Removalists Melbourne"
    subtitle="Locally owned Melbourne movers for homes, apartments and offices, fully insured, on time and honestly priced."
    breadcrumbLabel="Melbourne"
    breadcrumbParent={{ label: "Locations", href: "/locations" }}
    schemaServiceType="Removalist Services"
    areaServed={["Melbourne"]}
    aboutHeading="Moving in Melbourne"
    overview={[
      "OZ MOVE CO. is a Melbourne based removalist working right across the metro area, from CBD towers and inner city terraces through to family homes in Doncaster, Point Cook, Brighton and Frankston.",
      "We know this city: the loading docks, the tram lined streets, the two hour lift bookings and the end of month rush. That local knowledge is what keeps your move on schedule and your quote honest.",
      "Every job comes with a trained crew, a fully equipped truck and full insurance cover. You get a clear price up front, a team that arrives when it says it will, and furniture that reaches the other end exactly as it left.",
    ]}
    includedHeading="Included on Every Melbourne Move"
    included={[
      { icon: Shield, label: "Furniture Protection" },
      { icon: Users, label: "Trained Local Crew" },
      { icon: Wrench, label: "Trolleys & Equipment" },
      { icon: Truck, label: "Fully Equipped Truck" },
      { icon: PackageCheck, label: "Careful Loading" },
      { icon: PackageOpen, label: "Careful Unloading" },
      { icon: Clock, label: "On Time Arrival" },
      { icon: Shield, label: "Full Insurance" },
    ]}
    whyUsHeading="Why Choose OZ MOVE CO. in Melbourne"
    whyUs={[
      { icon: MapPin, title: "Melbourne Owned", desc: "A local team, not an interstate call centre or franchise." },
      { icon: DollarSign, title: "Transparent Pricing", desc: "Clear hourly rates with no hidden fees or surprise surcharges." },
      { icon: Award, title: "Experienced Crews", desc: "Hundreds of Melbourne moves every year across every suburb." },
      { icon: Truck, title: "Right Sized Fleet", desc: "Vans for tight docks, large trucks for full family homes." },
      { icon: Zap, title: "Fast Response", desc: "Same day quotes and short notice bookings welcome." },
      { icon: Shield, title: "Fully Insured", desc: "Public liability and goods in transit cover on every job." },
      { icon: Clock, title: "Reliable Timing", desc: "Realistic windows we actually stick to." },
      { icon: HeartHandshake, title: "Friendly Service", desc: "Calm, tidy and respectful from first call to final walk through." },
    ]}
    extra={ServicesBlock}
    faqs={[
      { q: "Which Melbourne suburbs do you service?", a: "All of them, from the CBD, Southbank and Docklands through the inner north and east, out to Werribee, Craigieburn, Ringwood, Dandenong and Frankston." },
      { q: "How much do removalists cost in Melbourne?", a: "Most Melbourne moves run between $140 and $220 per hour depending on crew size and truck. Send us your inventory and postcodes for a fixed estimate." },
      { q: "Do you work weekends and public holidays?", a: "Yes. Saturdays and Sundays are our busiest days, and after hours moves are available for offices and apartments with restricted access windows." },
      { q: "Can you handle CBD and high rise buildings?", a: "Absolutely. We work with loading docks, security teams and lift bookings across most major Melbourne towers every week." },
      { q: "How far in advance should I book?", a: "One to two weeks is ideal, particularly for end of month and weekend slots. Same day and next day bookings are often possible." },
      { q: "Do you move outside metropolitan Melbourne?", a: "Yes. We regularly move to Geelong, Ballarat, Bendigo and the Mornington Peninsula, and interstate moves are available on request." },
      { q: "Are you insured?", a: "Yes. Public liability and goods in transit cover apply to every Melbourne job. A certificate of currency is available on request." },
    ]}
  />
);

export default MelbournePage;
